import { DynamicValue } from "@faedev/dynamic-values";
import { composeClassNames } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useCookies } from "react-cookie";
import { ReactElement } from "react-markdown/lib/react-markdown";
import Text from "src/components/Text";
import Translate from "src/components/Translate";
import constants from "src/constants";
import gutter from "src/functions/gutter";
import isFormValid from "src/functions/isFormValid";
import onChangeToValue from "src/functions/onChangeToValue";
import translate from "src/functions/translate";
import Small_cancel from "src/icons/Small-cancel";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sButtonClose } from "src/styles/sButtonClose.module.scss";
import { sButtons } from "src/styles/sButtons.module.scss";
import { sFormControl } from "src/styles/sFormControl.module.scss";
import { sFormLabel } from "src/styles/sFormLabel.module.scss";
import { sModal } from "src/styles/sModal.module.scss";
import { sModalAuth } from "src/styles/sModalAuth.module.scss";

type Props = {
  className?: (string | string) | null | undefined;
  alert?: ReactElement | null | undefined;
  openModalLogin: DynamicValue<boolean>;
  openModalRegister?: DynamicValue<boolean>;
  openModalResetPassword?: DynamicValue<boolean>;
};

function ModalLogin({
  className,
  alert,
  openModalLogin,
  openModalRegister,
  openModalResetPassword,
}: Props) {
  var [loginDataState, setLoginDataState] = useState({
    identifier: ``,
    password: ``,
  });
  var [cookies, setCookie] = useCookies(null);
  var [validated, setValidated] = useState(false);
  var [formerrors, setFormErrors] = useState({});
  var [resultState, setResultState] = useState(null);
  const { locale: lang } = useRouter();

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    let errors: any = {};
    if (!loginDataState.identifier) {
      errors.identifier = `Email address is required`;
    } else if (!/\S+@\S+\.\S+/.test(loginDataState.identifier)) {
      errors.identifier = `Email address is invalid`;
    }
    if (!loginDataState.password) {
      errors.password = `Password is required`;
    }
    setFormErrors(errors);
    return (async function () {
      return await (async function iife() {
        setValidated(true);
        isFormValid(`FormLogin`)
          ? await (async function iife() {
              setResultState(true);
              var result = await (() =>
                fetch(`${constants.Paths.apiUrl}/auth/local`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(loginDataState),
                }).then((res) => {
                  if (res.ok) {
                    return res.json();
                  } else {
                    setResultState(false);
                  }
                }))();
              if (result !== undefined) {
                setCookie(`auth-token`, result.jwt, constants.CookiesOptions);
                openModalLogin.change(false);
                location.reload();
              }
            })()
          : handleSubmit;
      })();
    })();
  };

  return (
    <Modal
      show={openModalLogin.value}
      className={composeClassNames([
        sModal,
        sModalAuth,
        className,
        "modal-login",
      ])}
      size={`sm`}
      onHide={() => openModalLogin.change(false)}
    >
      <Modal.Header>
        <Modal.Title>
          <Translate tKey={`Sign in to Anandita`} />
        </Modal.Title>
        <Button
          variant={`link`}
          onClick={function () {
            return openModalLogin.change(false);
          }}
          size={`sm`}
          className={composeClassNames([sButtonClose, `btn-close`])}
        >
          <Small_cancel />
        </Button>
      </Modal.Header>
      <Modal.Body>
        {alert && alert}
        <Form
          id={`FormLogin`}
          validated={validated}
          noValidate
          onSubmit={handleSubmit}
        >
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`User name`} />
            </Form.Label>
            <Form.Control
              type={`email`}
              className={sFormControl}
              placeholder={translate(`Enter email`, lang)}
              name={`identifier`}
              id={`identifier`}
              onChange={function (param2) {
                return setLoginDataState({
                  identifier: onChangeToValue(param2),
                  password: loginDataState.password,
                });
              }}
              value={loginDataState.identifier}
              required={true}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate
                tKey={
                  formerrors.hasOwnProperty(`identifier`) &&
                  formerrors[`identifier`]
                }
              />
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label
              className={composeClassNames([
                sFormLabel,
                "d-flex",
                "justify-content-between",
                "align-items-center",
              ])}
            >
              <Translate tKey={`Password`} />
              <Button
                variant={`link`}
                size="sm"
                className={sButton}
                onClick={() => {
                  openModalLogin.change(false);
                  openModalRegister.change(false);
                  openModalResetPassword.change(true);
                }}
              >
                <Translate tKey={`Forgot password?`} />
              </Button>
            </Form.Label>
            <Form.Control
              type={`password`}
              placeholder={translate(`Enter password`, lang)}
              className={sFormControl}
              name={`password`}
              id={`password`}
              onChange={function (param2) {
                return setLoginDataState({
                  identifier: loginDataState.identifier,
                  password: onChangeToValue(param2),
                });
              }}
              value={loginDataState.password}
              required={true}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate
                tKey={
                  formerrors.hasOwnProperty(`password`) &&
                  formerrors[`password`]
                }
              />
            </Form.Control.Feedback>
          </Form.Group>
          <div
            className={composeClassNames([sButtons, "buttons", "align-center"])}
          >
            <Button
              className={composeClassNames([sButton, "btn-submit"])}
              variant={`primary`}
              type="submit"
            >
              <Translate tKey={`Sign in`} />
            </Button>
            {validated && !resultState ? (
              <Alert
                variant={`danger`}
                className={sAlert}
                style={gutter(`margin`, `vertical`, `md`)}
              >
                <Translate tKey={`You have entered invalid login details.`} />
              </Alert>
            ) : null}
            <Button
              variant={`link`}
              size="sm"
              className={sButton}
              onClick={() => {
                openModalLogin.change(false);
                openModalRegister.change(true);
                openModalResetPassword.change(false);
              }}
            >
              <Translate tKey={`Don't have an account? Sign up.`} />
            </Button>
          </div>
        </Form>
      </Modal.Body>
      {constants.Show.navSocialLogin ? (
        <Modal.Footer>
          <div
            className={composeClassNames([
              sButtons,
              "buttons",
              "column",
              "align-center",
            ])}
          >
            <Button
              type={`button`}
              className={composeClassNames([sButton, "btn-social"])}
              variant={`outline-dark`}
            >
              <Translate tKey={`Sign in via`} />
              {` `}
              <Text
                width={`bold`}
                size={`md`}
                style={{ marginLeft: constants.Size.xs }}
                className={`brand-facebook`}
              >{`Facebook`}</Text>
            </Button>
            <Button
              type={`button`}
              className={composeClassNames([sButton, "btn-social"])}
              variant={`outline-dark`}
            >
              <Translate tKey={`Sign in via`} />
              {` `}
              <Text
                width={`bold`}
                size={`md`}
                style={{ marginLeft: constants.Size.xs }}
                className={`brand-google`}
              >{`Google`}</Text>
            </Button>
          </div>
        </Modal.Footer>
      ) : null}
    </Modal>
  );
}

export default ModalLogin;
